import { tracked } from "@glimmer/tracking";
import { Task, task, restartableTask } from "ember-concurrency";
import Component from "@glimmer/component";
import { service } from "@ember/service";
import { action } from "@ember/object";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import PrivateNetworkSegmentModel from "../../../models/private-network-segment";
import { func, instanceOf, shape } from "prop-types";
import ENV from "glesys-controlpanel/config/environment";

@forbidExtraArgs
export default class ModalsPrivateNetworkSegmentEdit extends Component {
  @service modal;
  @service http;
  @service abilities;

  @arg(
    shape({
      segment: instanceOf(PrivateNetworkSegmentModel).isRequired,
      task: instanceOf(Task).isRequired,
      onRoutableChange: func,
    }).isRequired,
  )
  params;

  @tracked name = this.segment.name;
  @tracked routable = this.segment.routable;
  @tracked estimate;

  initialRoutable = this.segment.routable;

  get segment() {
    return this.params.segment;
  }

  get task() {
    return this.params.task;
  }

  get privateNetwork() {
    return this.segment.privateNetwork;
  }

  get routableSegmentsCount() {
    let segments = this.privateNetwork.hasMany("segments").value() ?? [];
    let others = segments.filter((segment) => segment.id !== this.segment.id && segment.routable).length;

    return others + (this.routable ? 1 : 0);
  }

  get isDisablingRouting() {
    return this.initialRoutable && !this.routable;
  }

  get costAmount() {
    return this.estimate?.content?.billing?.diff?.total ?? 0;
  }

  get project() {
    return this.segment.privateNetwork.project;
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.project?.organization);
  }

  estimateCostTask = restartableTask(async () => {
    if (!this.segment.supportsRouting || this.hasResellerPricing) {
      return;
    }

    this.estimate = await this.http.request({
      url: `${ENV.apiBaseUri}/privatenetwork/estimatedcost`,
      method: "POST",
      body: {
        projectkey: this.privateNetwork.project.id,
        privatenetworkid: this.privateNetwork.id,
        routablesegments: this.routableSegmentsCount,
      },
    });
  });

  onChange = ({ name }) => (this.name = name);

  @action
  selectRouting(routable) {
    this.routable = routable;

    this.estimateCostTask.perform();
  }

  submitTask = task(async () => {
    let routableChanged = this.routable !== this.initialRoutable;

    await this.task.perform({ name: this.name, routable: this.routable });

    if (routableChanged) {
      await this.params.onRoutableChange?.();
    }

    this.modal.close();
  });
}
